import React, { Component, Suspense, lazy } from 'react';
import qs from 'query-string';
import get from 'lodash.get';
import { Switch, Redirect } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import m from 'moment-timezone';

import { PAGE_TITLE, MODAL_EMAIL_CONFIRMATION, MODAL_PASSWORD_RESET, MODAL_NEWSLETTER_OPT_IN } from './constants/app';
import { EXCHANGE_BALANCE_CODE } from './constants/catalog';
import { DASHBOARD_SLUGS } from './constants/dashboards';

import BlankLayout from './layout/blank';
import MainLayout from './layout/main';
import NarrowLayout from './layout/narrow';
import StaticLayout from './layout/static';
import SidebarLayout from './layout/sidebar';

import AppRoute from './components/appRoute';
import AuthModals from './components/authModals';
import CookieBanner from './components/cookieBanner';
import NewsletterOptInModal from './components/newsletterOptInModal';
import Notification from './components/notification';
import PrivateRoute from './components/privateRoute';
import RequestFavoritesData from './components/requestFavoritesData';
import SplashScreen from './components/splashScreen';
import UpgradeToProfessionalModal from './components/upgradeToProfessionalModal';
import UserDataGate from './components/userDataGate';
import IdleReload from './components/idleReload';

import ErrorPage from './containers/error';
import Pricing from './containers/pricing';
import Settings from './containers/settings';

import GlobalStyle from './style';
import AntGlobalStyle from './styleAnt';

import * as params from './constants/params';

const Compare = lazy(() => import('./containers/compare' /* webpackChunkName: "compare" */));
const Dashboards = lazy(() => import('./containers/dashboards' /* webpackChunkName: "dashboards" */));
const Metrics = lazy(() => import('./containers/metrics' /* webpackChunkName: "metrics" */));
const Pulse = lazy(() => import('./containers/pulse' /* webpackChunkName: "pulse" */));
const Reports = lazy(() => import('./containers/reports' /* webpackChunkName: "reports" */));

class App extends Component {
  componentDidMount() {
    m.tz.setDefault('Etc/UTC');
  }

  modalRedirect = (modal) => (props) => {
    const secret = get(props, 'match.params.secret');
    const qsObject = {
      [params.MODAL]: modal,
      [params.SECRET]: secret,
    };

    return (
      <Redirect
        to={{
          pathname: '/metrics',
          search: qs.stringify(qsObject),
        }}
      />
    );
  };

  explorerRedirect(props) {
    const { a } = qs.parse(props.location.search);
    const qsObject = {
      a,
      m: EXCHANGE_BALANCE_CODE,
    };

    return (
      <Redirect
        to={{
          pathname: '/metrics',
          search: qs.stringify(qsObject),
        }}
      />
    );
  }

  explorerTransferRedirect(props) {
    const a = get(props.match.params, 'asset', 'BTC');
    const qsObject = {
      a,
      m: EXCHANGE_BALANCE_CODE,
    };

    return (
      <Redirect
        to={{
          pathname: '/metrics',
          search: qs.stringify(qsObject),
        }}
      />
    );
  }

  renderMetrics(props) {
    return <Metrics {...props} />;
  }

  renderCompare(props) {
    return <Compare {...props} />;
  }

  renderDashboards(props) {
    const name = get(props, 'match.params.name');

    if (!DASHBOARD_SLUGS.includes(name)) {
      return <Redirect to="/dashboards/btc-core-on-chain" />;
    }

    return <Dashboards {...props} />;
  }

  renderSettings(props) {
    return <Settings {...props} />;
  }

  renderPulse(props) {
    return <Pulse {...props} />;
  }

  renderReports(props) {
    return <Reports {...props} />;
  }

  render() {
    return (
      <UserDataGate>
        <Helmet>
          <title>{PAGE_TITLE}</title>
        </Helmet>
        <Suspense fallback={<SplashScreen />}>
          <Switch>
            <AppRoute
              component={this.explorerTransferRedirect}
              layout={MainLayout}
              path="/explorer/:asset/transfer/:transfer"
            />
            <AppRoute component={this.explorerRedirect} exact layout={MainLayout} path="/explorer" />

            <AppRoute component={this.renderPulse} exact layout={MainLayout} path="/" />
            <AppRoute component={this.renderReports} layout={MainLayout} path="/reports" />
            <AppRoute component={this.renderMetrics} layout={SidebarLayout} path="/metrics" />
            <AppRoute component={this.renderCompare} layout={SidebarLayout} path="/compare" />

            <AppRoute component={this.renderDashboards} layout={SidebarLayout} path="/dashboards/:name" />
            <Redirect from="/dashboards" to="/dashboards/btc-core-on-chain" />

            <PrivateRoute component={this.renderSettings} layout={NarrowLayout} path="/settings/:setting" />

            <AppRoute component={Pricing} layout={StaticLayout} path="/pricing" />
            <AppRoute component={ErrorPage} layout={BlankLayout} path="/404" />

            {/* Redirects */}
            <AppRoute
              component={this.modalRedirect(MODAL_EMAIL_CONFIRMATION)}
              layout={SidebarLayout}
              path="/email-confirmation/:secret"
            />

            <AppRoute
              component={this.modalRedirect(MODAL_NEWSLETTER_OPT_IN)}
              layout={SidebarLayout}
              path="/newsletter-confirmation/:secret"
            />

            <AppRoute
              component={this.modalRedirect(MODAL_PASSWORD_RESET)}
              layout={SidebarLayout}
              path="/password-reset/:secret"
            />

            <Redirect to="/" />
          </Switch>
        </Suspense>

        <CookieBanner />
        <Notification />

        <AuthModals />
        <NewsletterOptInModal />
        <UpgradeToProfessionalModal />

        <AntGlobalStyle />
        <GlobalStyle />

        <IdleReload />

        <RequestFavoritesData />
      </UserDataGate>
    );
  }
}

export default App;
